// @ts-nocheck
// @ts-ignore
import { Component } from '@theme/component';

/**
 * @typedef {object} ShareButtonRefs
 * @property {HTMLDetailsElement} [details]
 * @property {HTMLElement} [summary]
 * @property {HTMLInputElement} [input]
 * @property {HTMLButtonElement} [copyButton]
 * @property {HTMLButtonElement} [closeButton]
 * @property {HTMLElement} [status]
 */

/**
 * Share button web component.
 *
 * Uses the native navigator.share API when available.
 * Falls back to a <details> panel with a copy-to-clipboard UI.
 *
 * @extends {Component<ShareButtonRefs>}
 */
class ShareButton extends Component {
  /** @type {string} */
  #urlToShare = '';

  connectedCallback() {
    super.connectedCallback();

    const { input, details, summary } = this.refs;

    this.#urlToShare =
      this.dataset.shareUrl ||
      (input && input.value) ||
      (typeof window !== 'undefined' ? window.location.href : '');

    if (input && !input.value) {
      input.value = this.#urlToShare;
    }

    // Always wire summary click so we control open/share (fixes Firefox and native <details> quirks)
    if (summary instanceof HTMLElement) {
      summary.addEventListener('click', this.#handleSummaryClick);
    }
    if (details instanceof HTMLDetailsElement) {
      details.addEventListener('toggle', this.#handleDetailsToggle);
    }
    if (this.refs.copyButton instanceof HTMLButtonElement) {
      this.refs.copyButton.addEventListener('click', this.#handleCopyClick);
    }
    if (this.refs.closeButton instanceof HTMLButtonElement) {
      this.refs.closeButton.addEventListener('click', this.#handleCloseClick);
    }
  }

  disconnectedCallback() {
    const { details, summary, copyButton, closeButton } = this.refs;

    if (details instanceof HTMLDetailsElement) {
      details.removeEventListener('toggle', this.#handleDetailsToggle);
    }
    if (summary instanceof HTMLElement) {
      summary.removeEventListener('click', this.#handleSummaryClick);
    }
    if (copyButton instanceof HTMLButtonElement) {
      copyButton.removeEventListener('click', this.#handleCopyClick);
    }
    if (closeButton instanceof HTMLButtonElement) {
      closeButton.removeEventListener('click', this.#handleCloseClick);
    }

    super.disconnectedCallback();
  }

  /**
   * Public API: update the URL to share and sync the input field.
   * @param {string} url
   */
  updateUrl(url) {
    this.#urlToShare = url;
    const { input } = this.refs;
    if (input instanceof HTMLInputElement) {
      input.value = url;
    }
  }

  #resetState() {
    const { status, closeButton } = this.refs;

    if (status instanceof HTMLElement) {
      status.textContent = '';
      status.hidden = true;
    }

    if (closeButton instanceof HTMLButtonElement) {
      closeButton.hidden = true;
    }
  }

  #openFallbackDetails() {
    const { details } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      details.open = true;
    }
  }

  /**
   * Single summary click handler: try native share when available, otherwise open copy panel.
   * Prevents default so we control opening (fixes Firefox where native <details> can be unreliable).
   * navigator.share() is invoked synchronously to preserve user gesture.
   */
  #handleSummaryClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const { details } = this.refs;
    if (details instanceof HTMLDetailsElement && details.open) {
      details.open = false;
      return;
    }

    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      const sharePromise = navigator.share({
        url: this.#urlToShare,
        title: document.title,
      });
      sharePromise.catch((_error) => {
        this.#openFallbackDetails();
      });
      return;
    }

    this.#openFallbackDetails();
  };

  #handleDetailsToggle = () => {
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;

    if (!details.open) {
      this.#resetState();
    }
  };

  #handleCopyClick = async () => {
    const { input, status, closeButton } = this.refs;

    if (!(input instanceof HTMLInputElement)) return;

    const textToCopy = input.value || this.#urlToShare;
    if (!textToCopy) return;

    let copied = false;

    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(textToCopy);
        copied = true;
      } catch {
        copied = false;
      }
    }

    if (!copied) {
      // Fallback for very old browsers – best-effort only
      input.removeAttribute('readonly');
      input.focus();
      input.select();
      try {
        document.execCommand('copy');
        copied = true;
      } catch {
        copied = false;
      }
      input.setAttribute('readonly', 'readonly');
    }

    if (copied) {
      if (status instanceof HTMLElement) {
        status.textContent = status.dataset.messageCopied || 'Link copied to clipboard';
        status.hidden = false;
      }
      if (closeButton instanceof HTMLButtonElement) {
        closeButton.hidden = false;
        closeButton.focus();
      }
    }
  };

  #handleCloseClick = () => {
    const { details } = this.refs;

    if (details instanceof HTMLDetailsElement) {
      details.open = false;
    }

    this.#resetState();
  };
}

if (!customElements.get('share-button')) {
  customElements.define('share-button', ShareButton);
}



